export const DEFAULT_ACWR_THRESHOLD_CONFIG = {
	colors: ["#FFC83D", "#31B452", "#d9534f"],
	segment_names: ["Underworked", "Optimal", "Overworked"],
	segments: 3,
	thresholds: [0.8, 1.3],
};

// A team payload can arrive before the config loads, so every helper falls
// back to the default rather than throwing. A config without a colors array
// also falls back, because every color lookup indexes into it.
export const getSafeAcwrConfig = (config) => {
	if (!config || !Array.isArray(config.thresholds) || config.thresholds.length === 0) {
		return DEFAULT_ACWR_THRESHOLD_CONFIG;
	}
	if (!Array.isArray(config.colors) || config.colors.length === 0) {
		return DEFAULT_ACWR_THRESHOLD_CONFIG;
	}
	return config;
};

export const getAcwrLimits = (config) => {
	const { thresholds } = getSafeAcwrConfig(config);
	return { lower: thresholds[0], upper: thresholds[thresholds.length - 1] };
};

export const getAcwrSegmentIndex = (value, config) => {
	const { thresholds } = getSafeAcwrConfig(config);
	const lastIndex = thresholds.length - 1;
	for (let i = 0; i < thresholds.length; i += 1) {
		// The final threshold is exclusive, so a ratio exactly on it stays in the
		// segment below. This preserves the old rule, where only `value >
		// upperLimit` was overworked. Every other threshold is inclusive-lower.
		const belowSegmentTop = i === lastIndex ? value <= thresholds[i] : value < thresholds[i];
		if (belowSegmentTop) return i;
	}
	return thresholds.length;
};

export const getAcwrColor = (value, config) => {
	if (value === undefined || value === null || !Number.isFinite(Number(value))) return "#d3d3d3";
	const { colors } = getSafeAcwrConfig(config);
	const index = getAcwrSegmentIndex(value, config);
	return colors[index] || colors[colors.length - 1];
};

// The Team row radial chart paints the chronic load with the bottom segment
// color and the acute load with the middle segment color.
export const getAcwrRadialColors = (config) => {
	const { colors } = getSafeAcwrConfig(config);
	return { chronic: colors[0], acute: colors[Math.floor((colors.length - 1) / 2)] };
};

export const ACWR_PLOT_LINE_ID_PREFIX = "acwr_threshold_";

export const getAcwrPlotLines = (config) => {
	const { thresholds, colors } = getSafeAcwrConfig(config);
	return thresholds.map((threshold, index) => ({
		id: `${ACWR_PLOT_LINE_ID_PREFIX}${index}`,
		dashStyle: "ShortDash",
		// Color each line with the segment it opens, so N-1 lines stay readable.
		// The default 3-segment config gives green at the lower threshold and red
		// at the upper one, which is what the two fixed lines used to show.
		color: colors[index + 1] || "red",
		value: threshold,
		zIndex: 5,
	}));
};

// A chart toggles ACWR off, so it must drop every configured line, not just two.
export const removeAcwrPlotLines = (axis, config) => {
	if (!axis || typeof axis.removePlotLine !== "function") return;
	getAcwrPlotLines(config).forEach(({ id }) => axis.removePlotLine(id));
	// Older charts stored two fixed ids. Remove them so a saved chart definition
	// does not keep a stale band.
	["acwr_upper", "acwr_lower"].forEach((id) => axis.removePlotLine(id));
};

// Custom chart definitions carry their own hardcoded ACWR bands. Drop any band
// this module owns before it appends the configured ones, so a custom config
// does not render stale lines beside the new ones.
export const withoutAcwrPlotLines = (plotLines) => {
	if (!Array.isArray(plotLines)) return [];
	return plotLines.filter(({ id }) =>
		id !== "acwr_upper" && id !== "acwr_lower" &&
		!(typeof id === "string" && id.startsWith(ACWR_PLOT_LINE_ID_PREFIX))
	);
};
